/**
 * Every piece of website content that the admin can edit.
 * - `default` is what the site shows until the admin changes it (and what "Reset" restores).
 * - Pages read values through useContent().get(key); the admin editor is generated from this file.
 * Field types: text | textarea | url | image | list (one string per line) | objects (repeatable group of `fields`).
 * Keys must look like  section.name  (lowercase, digits, underscores).
 */
const GALLERY_DEFAULT = [
  "suite1-hero", "lounge1", "suite1-angle2", "kitchen1", "suite1-angle3", "washroom1", "suite2-hero", "kitchen2", "suite2-angle2",
  "washroom2", "suite1-angle4", "lounge2", "suite2-angle3", "kitchen3", "suite2-angle4", "washroom3", "gear-detail", "snack-detail",
].map((n, i) => ({ src: `/Images/${n}.png`, caption: "", h: i % 2 === 0 ? "tall" : "short" }));

export const SECTIONS = [
  {
    id: "site",
    title: "Contact details & brand",
    help: "Shown in the footer, contact page, floating WhatsApp button and search-engine data.",
    fields: [
      { key: "site.phone", label: "Phone (as displayed)", type: "text", default: "+91 7018595304" },
      { key: "site.phone_link", label: "Phone (for tap-to-call, digits with +)", type: "text", default: "+917018595304" },
      { key: "site.whatsapp", label: "WhatsApp number (digits, with country code)", type: "text", default: "917018595304" },
      { key: "site.whatsapp_greeting", label: "WhatsApp first message", type: "text", default: "Hi, I would like to enquire about a booking" },
      { key: "site.email", label: "Email", type: "text", default: "contact@roomxiii.com" },
      { key: "site.address", label: "Address (footer)", type: "textarea", default: "Patiala Road\nZirakpur, Punjab" },
      { key: "site.location_short", label: "Location (contact page)", type: "text", default: "Zirakpur, Patiala Road" },
      { key: "site.location_note", label: "Location note", type: "text", default: "Exact address shared on confirmation" },
      { key: "site.map_query", label: "Map search text", type: "text", default: "Patiala Road Zirakpur Punjab India" },
      { key: "site.hours", label: "Availability", type: "text", default: "24 / 7" },
      { key: "site.instagram_url", label: "Instagram link", type: "url", default: "https://www.instagram.com/official_roomxiii?utm_source=qr&igsh=aWZuOTgxM2FhdnQ3" },
      { key: "site.footer_tagline", label: "Footer description", type: "textarea", default: "An intimate, private retreat designed exclusively for couples seeking a discreet, cinematic luxury experience in the heart of the city." },
    ],
  },
  {
    id: "home",
    title: "Home page",
    help: "The headline, intro, scrolling ticker, scenes and closing call-to-action.",
    fields: [
      { key: "home.hero_line1", label: "Headline - first part (gold)", type: "text", default: "Where the night" },
      { key: "home.hero_line2", label: "Headline - second part (red)", type: "text", default: "belongs to two." },
      { key: "home.hero_text", label: "Intro paragraph", type: "textarea", default: "One exquisite private room. Behind a single door — cinematic ambience, total discretion, and every detail curated for intimate luxury." },
      { key: "home.hero_tagline", label: "Intro highlight", type: "text", default: "Private · Luxurious · Unforgettable." },
      { key: "home.cta_primary", label: "Main button", type: "text", default: "Reserve The Room" },
      { key: "home.cta_secondary", label: "Second button", type: "text", default: "Step Inside" },
      { key: "home.hero_image", label: "Hero background image", type: "image", default: "/Images/suite1-hero.png" },
      { key: "home.marquee", label: "Scrolling ticker (one phrase per line)", type: "list", default: ["Private Suite", "Self Check-in", "Mood Lighting", "Soundproof", "Hourly & Overnight", "Zirakpur", "Total Discretion", "One Room · One Couple"] },
      {
        key: "home.scenes", label: "Evening scenes", type: "objects", itemLabel: "Scene",
        fields: [{ key: "t", label: "Title", type: "text" }, { key: "d", label: "Description", type: "textarea" }, { key: "img", label: "Image", type: "image" }],
        default: [
          { t: "Arrive", d: "A smart-lock code, a quiet corridor, no one to meet.", img: "/Images/suite1-detail.png" },
          { t: "Unwind", d: "Lights dim to amber. Music, drinks and the lounge are yours.", img: "/Images/lounge1.png" },
          { t: "Ignite", d: "Rain shower for two, deep linens, a door that stays closed.", img: "/Images/washroom1.png" },
          { t: "Linger", d: "No rush at the end. Slip out as privately as you came in.", img: "/Images/kitchen1.png" },
        ],
      },
      { key: "home.cta_eyebrow", label: "Closing section - small title", type: "text", default: "Reserve Your Evening" },
      { key: "home.cta_heading", label: "Closing section - heading", type: "text", default: "Cross the threshold into" },
      { key: "home.cta_heading_accent", label: "Closing section - highlighted words", type: "text", default: "something cinematic." },
      { key: "home.cta_text", label: "Closing section - text", type: "textarea", default: "One room. One reservation per evening. Live availability — secure your moment now." },
      { key: "home.cta_image", label: "Closing section image", type: "image", default: "/Images/suite1-detail.png" },
    ],
  },
  {
    id: "room",
    title: "Room page",
    help: "Prices, guests, check-in/out times and rules are edited under 'Property & Rules'.",
    fields: [
      { key: "room.description", label: "Description", type: "textarea", default: "A private, couples-only retreat designed for total immersion — where cinematic ambience, absolute discretion and curated luxury converge behind a single door." },
      { key: "room.area_sqft", label: "Size (sq ft)", type: "text", default: "600" },
      { key: "room.beds", label: "Bed", type: "text", default: "1 king bed" },
      { key: "room.hero_image", label: "Main photo", type: "image", default: "/Images/suite2-hero.png" },
      { key: "room.hero_caption", label: "Photo caption", type: "text", default: "Cinematic ambience crafted for two" },
    ],
  },
  {
    id: "gallery",
    title: "Gallery page",
    help: "Add, remove and reorder photos. Upload from your computer.",
    fields: [
      { key: "gallery.title", label: "Page title", type: "text", default: "Gallery" },
      { key: "gallery.subtitle", label: "Subtitle", type: "text", default: "A glimpse into your private retreat." },
      {
        key: "gallery.images", label: "Photos", type: "objects", itemLabel: "Photo",
        fields: [{ key: "src", label: "Photo", type: "image" }, { key: "caption", label: "Caption (optional)", type: "text" }, { key: "h", label: "Shape (tall or short)", type: "text" }],
        default: GALLERY_DEFAULT,
      },
    ],
  },
  {
    id: "faq",
    title: "FAQ",
    help: "Questions and answers on the FAQ page.",
    fields: [
      {
        key: "faq.items", label: "Questions", type: "objects", itemLabel: "Question",
        fields: [{ key: "q", label: "Question", type: "text" }, { key: "a", label: "Answer", type: "textarea" }],
        default: [
          { q: "Is my privacy maintained?", a: "Absolutely. We operate one suite at a time. Self check-in via smart lock means no front-desk interaction. Our walls are soundproofed and our windows have blackout curtains." },
          { q: "Are IDs required?", a: "Yes — both guests must present valid government-issued IDs at the time of booking confirmation. This is for safety and compliance only; data is encrypted and never shared." },
          { q: "How do I pay?", a: "After you reserve, you'll see our payment QR code. Pay, then send the screenshot to us on WhatsApp and we'll confirm your booking." },
          { q: "What are check-in and check-out timings?", a: "Check-in and check-out times are shown on the Rules page and on every booking. Hourly stays run for the slot you booked, with a short buffer between guests." },
          { q: "Can I book by the hour?", a: "Yes. We offer hourly bookings — see the booking page for the minimum duration and current rate." },
          { q: "What is your cancellation policy?", a: "All bookings are non-refundable once confirmed. You can still cancel to release the dates, but no refund is issued. See our Cancellation Policy page for details." },
          { q: "Do you allow more than two guests?", a: "Our suite is strictly designed and licensed for two adults only." },
          { q: "Is the suite cleaned between guests?", a: "Yes — UV-sterilized linens and hospital-grade deep cleaning between every stay, with a buffer between guests." },
        ],
      },
    ],
  },
  {
    id: "about",
    title: "About page",
    help: "Our story, key numbers, the four pillars and the closing promise.",
    fields: [
      { key: "about.hero_line1", label: "Headline - first line", type: "text", default: "One room." },
      { key: "about.hero_line2", label: "Headline - second line (gold)", type: "text", default: "Infinite privacy." },
      { key: "about.hero_text", label: "Intro", type: "textarea", default: "ROOM XIII was built for couples who deserve better than a generic hotel room and a judgemental front desk. We created a single, obsessively designed private space — and one promise: whatever happens in Room XIII, stays in Room XIII." },
      {
        key: "about.stats", label: "Key numbers", type: "objects", itemLabel: "Number",
        fields: [{ key: "value", label: "Value", type: "text" }, { key: "label", label: "Label", type: "text" }],
        default: [{ value: "500+", label: "Stays completed" }, { value: "4.9", label: "Average rating" }, { value: "24/7", label: "Concierge support" }, { value: "100%", label: "Privacy guaranteed" }],
      },
      {
        key: "about.pillars", label: "Pillars (the first four use the existing icons)", type: "objects", itemLabel: "Pillar",
        fields: [{ key: "title", label: "Title", type: "text" }, { key: "desc", label: "Description", type: "textarea" }],
        default: [
          { title: "Couple-First Design", desc: "Every detail — the lighting, the music, the layout — is chosen with two people in mind. Not a family, not a business traveller. Just you." },
          { title: "Absolute Discretion", desc: "No front desk, no judgemental eyes. Smart-lock self check-in, soundproofed walls, and a strict guest-only entry policy." },
          { title: "Verified Safety", desc: "Every guest is ID-verified. CCTV covers the building entrance only — never inside. Your privacy is a non-negotiable." },
          { title: "Premium Standards", desc: "Hotel-grade linen, hospital-level hygiene protocols, and amenities curated for indulgence — not just comfort." },
        ],
      },
      { key: "about.promise", label: "Closing promise", type: "text", default: "Whatever happens in Room XIII, stays in Room XIII." },
    ],
  },
];

export const DEFAULTS = Object.fromEntries(SECTIONS.flatMap((s) => s.fields.map((f) => [f.key, f.default])));
